import { useOnValueChange } from '@calendly/hooks'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { useCallback, useEffect, useLayoutEffect, useRef, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useDisableScrollRestoration, useInitialScroll, useScrollPosition, useVisibleDateGroups } from 'components/meetings/hooks'
import { ScrollToTodayButton } from 'components/meetings/scroll_to_today_button'
import { Periods } from 'constants/meetings'
import { trackContactInteraction } from 'features/contacts'
import { useUpcomingPreset } from 'features/meetings/hooks/use_upcoming_preset'
import { getPresetWeekScrollStartDate } from 'features/meetings/utils'
import { useTrack } from 'hooks/analytics_events'
import { useFilters } from 'hooks/meetings/filters'
import { usePackageNameMap } from 'hooks/use_package_name_map'
import { timezoneSelector } from 'selectors/current_user'
import {
  getMeetingsMetadata,
  meetingListIncludesDate,
  scrollToDate,
  scrollToToday,
  shouldScrollForDatePick,
} from 'shared/utils'

import { Bookend } from './bookend'
import { MeetingGroup } from './meeting_group'
import { Container, BottomPadding } from './styles'
import { MeetingBriefsBanner } from '../../meeting_briefs_banner'
/** Fallback when card’s scroll-margin-top cannot be read (matches --meeting-group-scroll-offset default) */
const DEFAULT_VIEWPORT_TOP_PX = 164

function scrollMeetingCardIntoView(cardId, containerRef) {
  const container = containerRef?.current
  const el = container?.querySelector(`[data-testid="meeting-card-${cardId}"]`)
  if (!el?.scrollIntoView) return
  const viewportTopPx = parseInt(getComputedStyle(el).scrollMarginTop, 10) || DEFAULT_VIEWPORT_TOP_PX
  const rect = el.getBoundingClientRect()
  const topNotInView = rect.top < viewportTopPx || rect.top >= window.innerHeight
  const bottomNotInView = rect.bottom > window.innerHeight
  let block = 'nearest'
  if (topNotInView || bottomNotInView) block = 'center'
  el.scrollIntoView({ block, behavior: 'smooth' })
}

export const MeetingContainer = ({
  meetings,
  onLoadPrevious,
  onLoadNext,
  showBuffers,
  selectedMeetingId,
  hasPrevious,
  hasNext,
  isNextLoading,
  isPreviousLoading,
  contactUuid = null,
  onboardingTourMeetingRefCallback,
  onboardingTourNotetakerRefCallback,
  firstUpcomingEventKey,
  todayDate,
  onVisibleDateRangeChange,
  onScrollAnchorDateChange,
  onUserScroll,
  onDateScrollSettled,
  scrollToDateTarget = null,
  isListFetching = false,
  isListLoading = false,
}) => {
  useDisableScrollRestoration()
  const containerRef = useRef(null)
  const timezone = useSelector(timezoneSelector)
  // Single-range presets (Today / This week / Last week) scope the list to that window, so a
  // "Scroll to today" affordance is meaningless and only overlaps the sticky date filters.
  // Upcoming keeps the button since the list paginates forward from today.
  const { presetFlagOn, presetPeriod, activePreset } = useUpcomingPreset()
  const { getFilterParams } = useFilters()
  const filterParams = getFilterParams()
  const isPhone = useDeviceType('isPhone')
  const hideScrollToToday = presetFlagOn && !!presetPeriod
  // Phone sticky Displaying N owns the list gap — hide first date badge + zero group margin.
  // Desktop/tablet keep the date badge but still flush the first day (no spacing2x under the count).
  const usePhoneStickyCountLayout = presetFlagOn && isPhone
  const usePresetListHeaderForFirstGroup = usePhoneStickyCountLayout
  const useDesktopPresetFirstGroup = !!activePreset && !isPhone
  // Always mark the feed when sticky count / preset chrome is up so first-day flush CSS applies.
  const presetFeed = usePhoneStickyCountLayout || !!activePreset

  const meetingsArray = useMemo(
    () => (Array.isArray(meetings) ? meetings : []),
    [meetings],
  )
  const initialScrollDate = useMemo(() => {
    if (filterParams?.period === Periods.FIXED && filterParams?.start_date) {
      return filterParams.start_date
    }
    if (!presetFlagOn || !presetPeriod) return null
    // Phone: keep the table under sticky Displaying N — do not scroll the first date away.
    if (isPhone) return null
    if (presetPeriod !== Periods.THIS_WEEK && presetPeriod !== Periods.LAST_WEEK) return null
    return getPresetWeekScrollStartDate(presetPeriod, timezone)
  }, [filterParams, presetFlagOn, presetPeriod, timezone, isPhone])
  const trackCenterDate = Boolean(onScrollAnchorDateChange)
  const { dateRange, centerDate, registerDateGroupRef, registerScrollAnchors } = useVisibleDateGroups(
    containerRef,
    meetingsArray,
    200,
    trackCenterDate,
    onUserScroll,
  )

  useEffect(() => {
    if (onVisibleDateRangeChange) {
      onVisibleDateRangeChange(dateRange)
    }
  }, [dateRange, onVisibleDateRangeChange])

  useEffect(() => {
    if (onScrollAnchorDateChange) {
      onScrollAnchorDateChange(centerDate)
    }
  }, [centerDate, onScrollAnchorDateChange])
  const dataLoadedRef = useRef(false)
  const meetingsSignatureRef = useRef(null)
  const lastScrolledPickRef = useRef(null)
  const track = useTrack()

  const isWeekPresetScroll = Boolean(initialScrollDate)
  const isListSettled = !isListFetching && !isListLoading

  const setScrollData = useScrollPosition(meetings, (scrollData) => {
    if (typeof window === 'undefined') return

    const heightDiff = document.documentElement.scrollHeight - scrollData.height
    window.scrollTo(0, scrollData.top + heightDiff)
  })

  useInitialScroll(
    containerRef,
    meetings,
    timezone,
    undefined,
    scrollToDateTarget,
    initialScrollDate,
    {
      isListSettled,
      // Phone sticky Displaying N: skip scrollToToday (centers now-line / opens white band).
      skipScrollToToday: usePhoneStickyCountLayout,
    },
  )

  useEffect(() => {
    lastScrolledPickRef.current = null
  }, [scrollToDateTarget])

  useLayoutEffect(() => {
    if (!scrollToDateTarget || dataLoadedRef.current || selectedMeetingId) return undefined
    if (lastScrolledPickRef.current === scrollToDateTarget) return undefined

    const listSettling = isListFetching || isListLoading
    if (listSettling) return undefined
    if (!shouldScrollForDatePick(scrollToDateTarget, meetingsArray)) {
      if (!listSettling) {
        lastScrolledPickRef.current = scrollToDateTarget
        onDateScrollSettled?.(scrollToDateTarget)
      }
      return undefined
    }

    const exactDayInList = meetingListIncludesDate(meetingsArray, scrollToDateTarget)

    let cancelled = false
    let alignRaf = null
    let settleOuterRaf = null
    let settleInnerRaf = null

    const attemptScroll = () => {
      if (cancelled) return null
      return scrollToDate(
        containerRef,
        scrollToDateTarget,
        false,
        meetingsArray,
        { exactPick: exactDayInList },
      )
    }

    const landed = attemptScroll()
    if (!landed) {
      lastScrolledPickRef.current = scrollToDateTarget
      onDateScrollSettled?.(scrollToDateTarget)
      return undefined
    }

    // Layout pass after the picked day's badge is hidden; second pass picks up feed-top metrics.
    alignRaf = requestAnimationFrame(() => {
      if (cancelled) return
      attemptScroll()
      requestAnimationFrame(() => {
        if (cancelled) return
        attemptScroll()
        lastScrolledPickRef.current = scrollToDateTarget
        if (!onDateScrollSettled) return
        settleOuterRaf = requestAnimationFrame(() => {
          settleInnerRaf = requestAnimationFrame(() => {
            if (!cancelled) onDateScrollSettled(scrollToDateTarget)
          })
        })
      })
    })

    return () => {
      cancelled = true
      if (alignRaf !== null) cancelAnimationFrame(alignRaf)
      if (settleOuterRaf !== null) cancelAnimationFrame(settleOuterRaf)
      if (settleInnerRaf !== null) cancelAnimationFrame(settleInnerRaf)
    }
  }, [
    scrollToDateTarget,
    meetingsArray,
    isListFetching,
    isListLoading,
    selectedMeetingId,
    containerRef,
    onDateScrollSettled,
  ])

  // Only scroll when the list data actually changed (e.g. initial load / re-center), not when
  // the reference changed due to selection (URL update) which would jump the list. When a date
  // was picked in the selector, scroll is handled by the effect above after pivot data loads.
  // Phone sticky Displaying N: do not call scrollToToday — it centers the now-line / first
  // day and leaves a large white band under the count on initial load (all presets).
  useOnValueChange(meetings, () => {
    if (dataLoadedRef.current || selectedMeetingId) return
    if (scrollToDateTarget) return
    if (isWeekPresetScroll) return
    if (usePhoneStickyCountLayout) return
    const arr = Array.isArray(meetings) ? meetings : []
    const signature = arr.length
      ? `${arr.length}-${arr[0]?.rawDate ?? ''}-${arr[arr.length - 1]?.rawDate ?? ''}`
      : ''
    if (meetingsSignatureRef.current === signature) return
    meetingsSignatureRef.current = signature
    scrollToToday(containerRef, meetings, timezone)
  })

  // When a meeting is selected, scroll so the full card is visible (below the header, not cut off at bottom)
  useEffect(() => {
    if (!selectedMeetingId || typeof window === 'undefined') return undefined
    const id = selectedMeetingId
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollMeetingCardIntoView(id, containerRef))
    })
    return () => cancelAnimationFrame(rafId)
  }, [selectedMeetingId])

  const handleLoadMoreTop = useCallback(() => {
    if (isPreviousLoading) return
    // track from Contact page
    if (contactUuid) {
      trackContactInteraction({
        track,
        location: 'interactions_feed_all',
        action: 'view_more_interactions',
        contactUuid
      })
    }
    if (typeof window === 'undefined') return

    setScrollData({
      height: document.documentElement.scrollHeight,
      top: window.scrollY || document.documentElement.scrollTop
    })

    dataLoadedRef.current = true
    onLoadPrevious()
  }, [isPreviousLoading, onLoadPrevious, setScrollData, track, contactUuid])

  const handleLoadMoreBottom = useCallback(() => {
    if (isNextLoading) return

    dataLoadedRef.current = true
    onLoadNext()
  }, [isNextLoading, onLoadNext])

  const eventUuids = useMemo(() => (Array.isArray(meetings)
    ? meetings.flatMap((group) => group.events?.map((e) => e.uuid).filter(Boolean) || [])
    : []), [meetings])
  const packageNameMap = usePackageNameMap(eventUuids)

  const {
    hasFutureEvents,
    hasMeetingInProgress
  } = useMemo(() => getMeetingsMetadata(meetings, timezone), [meetings, timezone])

  const scrollToTodayProps = {
    containerRef,
    meetings,
    timezone,
    contactUuid,
  }

  return (
    <Container presetFeed={presetFeed}>
      <div ref={containerRef}>
        {onLoadPrevious && (
          <Bookend
            onClick={handleLoadMoreTop}
            isLoading={isPreviousLoading}
            hasMore={hasPrevious}
          />
        )}
        {meetingsArray.map((meetingGroup, groupIndex) => (
          <MeetingGroup
            key={meetingGroup.date}
            date={meetingGroup.date}
            rawDate={meetingGroup.rawDate}
            events={meetingGroup.events || []}
            showBuffers={showBuffers}
            selectedMeetingId={selectedMeetingId}
            anyInProgress={hasMeetingInProgress}
            packageNameMap={packageNameMap}
            onboardingTourMeetingRefCallback={onboardingTourMeetingRefCallback}
            onboardingTourNotetakerRefCallback={onboardingTourNotetakerRefCallback}
            firstUpcomingEventKey={firstUpcomingEventKey}
            topContent={meetingGroup.rawDate === todayDate ? <MeetingBriefsBanner meetings={meetingGroup.events || []} /> : null}
            registerDateGroupRef={
              (onVisibleDateRangeChange || onScrollAnchorDateChange) ? registerDateGroupRef : undefined
            }
            registerScrollAnchors={onScrollAnchorDateChange ? registerScrollAnchors : undefined}
            usePresetListHeader={usePresetListHeaderForFirstGroup && groupIndex === 0}
            useDesktopPresetFirstGroup={useDesktopPresetFirstGroup && groupIndex === 0}
            /* First day stays flush under the top bookend / Displaying N (no spacing2x). */
            flushTop={groupIndex === 0}
          />
        ))}
        <Bookend
          onClick={handleLoadMoreBottom}
          isLoading={isNextLoading}
          hasMore={hasNext}
          anyFutureMeetings={hasFutureEvents}
          isUpcoming
        />
        <BottomPadding />
      </div>
      <ScrollToTodayButton
        {...scrollToTodayProps}
        presetMeetingAreaTop={presetFlagOn}
        isHidden={hideScrollToToday}
      />
    </Container>
  )
}
